import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api, errMsg, toPayload, isPastSlot, payText } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { BigButton } from "@/components/Layout";
import { ScrapPicker } from "@/components/ScrapPicker";
import { MapPicker } from "@/components/MapPicker";
import { AddressFields, AreaField } from "@/components/Fields";
import { SlotPicker } from "@/components/SlotPicker";
import { PaymentFields } from "@/components/PaymentFields";
import { PhotoInput } from "@/components/PhotoInput";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const Section = ({ title, children }) => (
  <section className="space-y-3 rounded-3xl border border-[#D2E0D8] bg-white p-4">
    <h2 className="font-display text-lg font-bold text-[#0D3B2B]">{title}</h2>
    {children}
  </section>
);

const changes = (o, f, t, slotLabel) => {
  const L = [];
  const add = (name, a, b) => String(a ?? "") !== String(b ?? "") && L.push({ name, from: a || "—", to: b || "—" });
  add(t("scrap types"), o.scrap_types.map(t).join(", "), f.scrap_types.map(t).join(", "));
  add(t("'Other' details"), o.other_text, f.other_text);
  add(t("approximate weight"), o.est_weight ? `${o.est_weight} kg` : "", f.est_weight ? `${f.est_weight} kg` : "");
  add(t("pickup date"), o.date, f.date);
  add(t("pickup time"), o.slot_label, slotLabel(f.slot));
  add(t("address"), o.address, f.address);
  add(t("landmark"), o.landmark, f.landmark);
  if (o.lat !== f.lat || o.lng !== f.lng) L.push({ name: t("map location"), from: t("old pin"), to: t("new pin") });
  const newPay = f.payment_method !== o.payment.method || f.upi_id || f.bank_account || f.bank_holder || f.bank_ifsc;
  if (newPay) L.push({ name: t("payment choice"), from: t(payText(o.payment)), to: t(f.payment_method === "Bank" ? "Online bank transfer" : f.payment_method) });
  add(t("note"), o.note, f.note);
  if (o.photo_path !== f.photo_path) L.push({ name: t("photo"), from: o.photo_path ? t("old photo") : "—", to: f.photo_path ? t("new photo") : "—" });
  return L;
};

export default function BookingEdit() {
  const { id } = useParams();
  const { t, meta } = useApp();
  const nav = useNavigate();
  const [orig, setOrig] = useState(null);
  const [form, setForm] = useState(null);
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const slotLabel = (s) => meta?.slots?.find((x) => x.value === s)?.label || s;

  useEffect(() => {
    api.get(`/bookings/${id}`).then(({ data }) => {
      if (["Completed", "Cancelled"].includes(data.status)) { toast.error(t("This pickup can no longer be changed.")); return nav(`/history/${id}`); }
      setOrig(data);
      setForm({ ...data, est_weight: data.est_weight ?? "", payment_method: data.payment.method, upi_id: "", bank_holder: "", bank_account: "", bank_ifsc: "" });
    }).catch((e) => { toast.error(errMsg(e)); nav("/history"); });
  }, [id]); // eslint-disable-line

  if (!form) return <p className="text-[#7B968B]">{t("Loading…")}</p>;

  const review = () => {
    if (!form.scrap_types.length) return toast.error(t("Please choose at least one scrap type."));
    if (!form.slot || isPastSlot(form.date, form.slot)) return toast.error(t("This time has already passed. Please choose a later time slot."));
    const d = changes(orig, form, t, slotLabel);
    if (!d.length) return toast(t("You haven't changed anything yet."));
    setDiff(d);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/bookings/${id}`, toPayload(form));
      toast.success(t("Pickup updated"));
      nav(`/history/${id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setDiff(null);
    } finally {
      setBusy(false);
    }
  };

  const title = diff?.length === 1
    ? `${t("Are you sure you want to change the")} ${diff[0].name} ${t("from")} ${diff[0].from} ${t("to")} ${diff[0].to}?`
    : t("Are you sure you want to make these changes?");

  return (
    <div className="space-y-4">
      <button onClick={() => nav(`/history/${id}`)} className="flex items-center gap-1 text-sm font-semibold text-[#2E9E6B]" data-testid="edit-back-btn">
        <ArrowLeft className="h-4 w-4" /> {orig.booking_id}
      </button>
      <h1 className="font-display text-3xl font-extrabold text-[#0D3B2B]">{t("Edit pickup")}</h1>
      <Section title={t("Scrap")}><ScrapPicker form={form} set={set} /></Section>
      <Section title={t("Location")}>
        <MapPicker lat={form.lat} lng={form.lng} onChange={(lat, lng) => set({ lat, lng })} />
        <AddressFields form={form} set={set} />
      </Section>
      <Section title={t("Date & time")}><SlotPicker form={form} set={set} /></Section>
      <Section title={t("Payment")}><PaymentFields form={form} set={set} saved={orig.payment} /></Section>
      <Section title={t("Extras")}>
        <AreaField label={t("Note for the pickup team (optional)")} testid="note-input" value={form.note} onChange={(e) => set({ note: e.target.value })} />
        <PhotoInput value={form.photo_path} onChange={(p) => set({ photo_path: p })} testid="booking-photo" />
      </Section>
      <BigButton onClick={review} data-testid="edit-save-btn">{t("Save changes")}</BigButton>
      <ConfirmDialog open={!!diff} onOpenChange={(o) => !o && setDiff(null)} busy={busy} onYes={save} title={title}
        lines={diff && diff.length > 1 ? diff.map((d) => `${d.name}: ${d.from} → ${d.to}`) : []} />
    </div>
  );
}
